import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Loader2, Target, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { useLeads } from '@/hooks/leads/useLeads';
import { isOpenLead } from '@/lib/leads/leadStatus';
import type { Lead } from '@/types/lead';

export interface SelectedLead {
  id: string;
  leadNumber: string;
  label: string;
  contactName?: string;
}

interface LeadSelectorProps {
  selected: SelectedLead | null;
  onSelect: (lead: Lead) => void;
  onClear: () => void;
  placeholder?: string;
  readOnly?: boolean;
}

function leadLabel(lead: Lead): string {
  return lead.title || lead.serviceRequested || lead.leadNumber;
}

export function LeadSelector({
  selected,
  onSelect,
  onClear,
  placeholder = 'Search by lead #, customer, or service...',
  readOnly = false,
}: LeadSelectorProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce the search term (~300ms), mirroring CustomerSelector.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  const searchActive = debouncedQuery.length >= 2;
  const leadsQuery = useLeads(
    { search: debouncedQuery, limit: 10, sortBy: 'createdAt', sortDir: 'desc' },
    { enabled: searchActive },
  );

  // Only offer open leads — the backend rejects closed/won/lost ones.
  const results = useMemo(
    () => (leadsQuery.data?.data ?? []).filter(isOpenLead),
    [leadsQuery.data?.data],
  );

  useEffect(() => {
    function handler(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (selected) {
    return (
      <div className="flex min-h-10 items-center gap-3 rounded-md border bg-card px-3 py-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Target className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {selected.leadNumber} — {selected.label}
          </p>
          {selected.contactName && (
            <p className="truncate text-xs text-muted-foreground">{selected.contactName}</p>
          )}
        </div>
        {!readOnly && (
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            title="Clear linked lead"
            aria-label="Clear linked lead"
            onClick={onClear}
            className="shrink-0"
          >
            <X className="size-3.5" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          placeholder={placeholder}
          value={query}
          disabled={readOnly}
          onChange={(event) => {
            setQuery(event.target.value);
            setShowResults(true);
          }}
          onFocus={() => results.length > 0 && setShowResults(true)}
          className="pl-8"
        />
        {searchActive && leadsQuery.isFetching && (
          <Loader2
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground"
          />
        )}
      </div>

      {showResults && searchActive && results.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-lg">
          {results.map((lead) => (
            <button
              key={lead.id}
              type="button"
              className="flex w-full items-start gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
              onClick={() => {
                setQuery('');
                setShowResults(false);
                onSelect(lead);
              }}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Target className="size-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {lead.leadNumber} — {leadLabel(lead)}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {lead.contact?.displayName || 'No contact'}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {showResults && searchActive && !leadsQuery.isFetching && results.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover px-3 py-4 text-center shadow-lg">
          <p className="text-sm text-muted-foreground">No open leads found</p>
        </div>
      )}
    </div>
  );
}
